/**
 * useChatProvenance — small hook that tells a page whether its current
 * filter values arrived via a chatbot pill click (`?_chat=1`) or via the
 * user's own action.
 *
 * Usage:
 *   const isChatProvenance = useChatProvenance();
 *   isChatProvenance && <span>🤖</span>
 *
 * When the user manually changes any filter, call `clearChatProvenance` (or
 * use `useClearChatOnManualFilter`) so the “from chat” badge / research
 * banner disappear. Chat-pill navigations set `_chat=1` again on the next click.
 */
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CHAT_PROVENANCE_PARAM } from './chatDirectives';

export function useChatProvenance(): boolean {
  const [searchParams] = useSearchParams();
  return searchParams.get(CHAT_PROVENANCE_PARAM) === '1';
}

/** Mutates `params` to drop the chat provenance flag. */
export function stripChatProvenanceParam(params: URLSearchParams): void {
  params.delete(CHAT_PROVENANCE_PARAM);
}

/**
 * Apply a string patch to URLSearchParams. Returns true if any key's value
 * actually changed — used so no-op writes (e.g. keyword debounce after a
 * chat-pill URL sync) do not strip `_chat`.
 */
export function applySearchParamPatch(
  params: URLSearchParams,
  patch: Record<string, string>,
): boolean {
  let changed = false;
  for (const [k, v] of Object.entries(patch)) {
    const cur = params.get(k) ?? '';
    const next = v || '';
    if (next) {
      if (cur !== next) {
        params.set(k, next);
        changed = true;
      }
    } else if (params.has(k)) {
      params.delete(k);
      changed = true;
    }
  }
  return changed;
}

/** Call from a filter-change handler to strip the chat flag from the URL. */
export function clearChatProvenance(
  setSearchParams: (
    params: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams),
    opts?: { replace?: boolean },
  ) => void,
  searchParams?: URLSearchParams,
): void {
  // Prefer the functional updater so callers don't need a fresh searchParams
  // snapshot (avoids stale closures on rapid filter clicks).
  if (searchParams && !searchParams.has(CHAT_PROVENANCE_PARAM)) return;
  setSearchParams(prev => {
    if (!prev.has(CHAT_PROVENANCE_PARAM)) return prev;
    const next = new URLSearchParams(prev);
    stripChatProvenanceParam(next);
    return next;
  }, { replace: true });
}

/**
 * Stable callback for list pages: strip `?_chat=1` when the user manually
 * edits a filter. Safe to call on every manual change — no-ops when the
 * flag is already absent.
 */
export function useClearChatOnManualFilter(): () => void {
  const [, setSearchParams] = useSearchParams();
  return useCallback(() => {
    clearChatProvenance(setSearchParams);
  }, [setSearchParams]);
}
